import { useState } from "react";
import ScrollToTop from "@/components/ScrollToTop";
import { useCanonical } from "@/hooks/use-canonical";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, ChevronRight, Mail, MapPin } from "lucide-react";
const jobcamLogo = "/jobcam-logo-400.webp";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { capitals, getCitiesByCapital } from "@/data/cities";

const productLinks = [
  { to: "/construction-site-cameras", label: "Construction Site Cameras" },
  { to: "/remote-monitoring-cameras", label: "Remote Monitoring Cameras" },
  { to: "/construction-time-lapse-cameras", label: "Time-Lapse Cameras" },
  { to: "/temporary-site-security-cameras", label: "Temporary Site Security" },
  { to: "/solar-site-cameras", label: "Solar Site Cameras" },
  { to: "/building-site-cctv", label: "Building Site CCTV" },
  { to: "/councils", label: "Councils" },
  { to: "/civil-works", label: "Civil Works" },
];

// Footer columns. Solutions leads with the three customer segments so
// /councils and /civil-works get a crawlable site-wide link from every page.
const footerSolutions = [
  { to: "/construction-site-cameras", label: "Construction Contractors" },
  { to: "/councils", label: "Councils & Local Government" },
  { to: "/civil-works", label: "Civil Works & Infrastructure" },
  { to: "/solar-site-cameras", label: "Solar Site Cameras" },
  { to: "/construction-time-lapse-cameras", label: "Time-Lapse Cameras" },
  { to: "/remote-monitoring-cameras", label: "Remote Monitoring" },
];

const footerResources = [
  { to: "/watch-how-jobcam-protects", label: "Watch the Demo" },
  { to: "/pricing", label: "Pricing" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/blog", label: "Blog" },
  { to: "/support", label: "Support" },
];

const footerCompany = [
  { to: "/about", label: "About JobCam" },
  { to: "/contact", label: "Contact" },
  { to: "/locations", label: "All Locations" },
];

const navLinks = [
  { to: "/pricing", label: "Pricing" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/support", label: "Support" },
  { to: "/contact", label: "Contact" },
];

// Build location groups for nav
const locationGroups = capitals.map((cap) => ({
  capital: { to: `/locations/${cap.slug}`, label: cap.name },
  cities: getCitiesByCapital(cap.slug).map((c) => ({ to: `/locations/${c.slug}`, label: c.name })),
}));

export default function Layout({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [expandedCapital, setExpandedCapital] = useState<string | null>(null);
  const [expandedDesktop, setExpandedDesktop] = useState<string | null>(null);
  const [expandedFooterState, setExpandedFooterState] = useState<string | null>(null);
  const location = useLocation();
  useCanonical();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ScrollToTop />
      <div className="border-b border-border bg-navy-deep">
        <div className="container flex items-center justify-between py-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-6">
            <a href="tel:1800562226" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone className="h-3.5 w-3.5" />
              <span>1800 JOBCAM</span>
            </a>
            <a href="mailto:support@jobcam.com.au" className="hidden sm:flex items-center gap-2 hover:text-primary transition-colors">
              <Mail className="h-3.5 w-3.5" />
              <span>support@jobcam.com.au</span>
            </a>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5" />
            <span>Serving All Major Australian Cities</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="container flex items-center justify-between py-4">
          <Link to="/" className="flex items-center">
            <img src={jobcamLogo} alt="JobCam Logo" className="h-10" width={191} height={40} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* Products dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors font-medium">
                Solutions <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="glass-card rounded-lg p-2 min-w-[260px] shadow-card">
                  {productLinks.map((link) => (
                    <Link key={link.to} to={link.to} className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors">
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Locations mega dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors font-medium">
                Locations <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="glass-card rounded-lg p-2 min-w-[260px] shadow-card">
                  {capitals.map((cap) => (
                    <Link key={cap.slug} to={`/locations/${cap.slug}`} className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors">
                      {cap.name}
                    </Link>
                  ))}
                  <button
                    onClick={() => document.getElementById('regional-cities')?.scrollIntoView({ behavior: 'smooth' })}
                    className="block w-full text-left px-4 py-2.5 text-sm text-primary hover:text-orange-glow hover:bg-secondary rounded-md transition-colors font-medium"
                  >
                    Regional Cities ↓
                  </button>
                </div>
              </div>
            </div>

            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className={`px-3 py-2 text-sm font-medium transition-colors ${location.pathname === link.to ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href="https://cloud.jobcam.com.au" target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="default" className="text-muted-foreground hover:text-foreground">
                Login
              </Button>
            </a>
            <a href="tel:1800562226">
              <Button variant="hero-outline" size="default">
                <Phone className="h-4 w-4" /> Call Now
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="hero" size="default">Get a Quote</Button>
            </Link>
          </div>

          {/* Mobile toggle */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-foreground" aria-label={mobileOpen ? "Close menu" : "Open menu"}>
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden border-t border-border overflow-hidden">
              <div className="container py-4 space-y-2 max-h-[70vh] overflow-y-auto">
                <button onClick={() => setProductsOpen(!productsOpen)} className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-muted-foreground">
                  Solutions <ChevronDown className={`h-4 w-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                </button>
                {productsOpen && productLinks.map((link) => (
                  <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)} className="block pl-6 py-2 text-sm text-muted-foreground hover:text-foreground">
                    {link.label}
                  </Link>
                ))}

                <button onClick={() => setLocationsOpen(!locationsOpen)} className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-muted-foreground">
                  Locations <ChevronDown className={`h-4 w-4 transition-transform ${locationsOpen ? 'rotate-180' : ''}`} />
                </button>
                {locationsOpen && locationGroups.map((group) => (
                  <div key={group.capital.to}>
                    <div className="flex items-center">
                      <Link to={group.capital.to} onClick={() => setMobileOpen(false)} className="flex-1 pl-6 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                        {group.capital.label}
                      </Link>
                      <button
                        onClick={() => setExpandedCapital(expandedCapital === group.capital.label ? null : group.capital.label)}
                        className="p-2 text-muted-foreground"
                        aria-label={`Show cities in ${group.capital.label}`}
                      >
                        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expandedCapital === group.capital.label ? 'rotate-90' : ''}`} />
                      </button>
                    </div>
                    {expandedCapital === group.capital.label && group.cities.map((city) => (
                      <Link key={city.to} to={city.to} onClick={() => setMobileOpen(false)} className="block pl-10 py-1.5 text-xs text-muted-foreground hover:text-foreground">
                        {city.label}
                      </Link>
                    ))}
                  </div>
                ))}

                {navLinks.map((link) => (
                  <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 flex flex-col gap-2">
                  <a href="https://cloud.jobcam.com.au" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}>
                    <Button variant="hero-outline" size="lg" className="w-full">Login</Button>
                  </a>
                  <Link to="/contact" onClick={() => setMobileOpen(false)}>
                    <Button variant="hero" size="lg" className="w-full">Get a Quote</Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="min-h-screen">{children}</main>

      {/* CTA Banner */}
      <section className="gradient-orange py-12">
        <div className="container text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
            Ready to Secure Your Construction Site?
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
            Get a free site assessment and custom quote for your construction project. Australia-wide installation available.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button variant="hero-outline" size="xl" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                Get Your Free Quote
              </Button>
            </Link>
            <a href="tel:1800562226">
              <Button variant="ghost" size="xl" className="text-primary-foreground hover:bg-primary-foreground/10">
                <Phone className="h-5 w-5" /> 1800 JOBCAM
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-deep border-t border-border">
        <div className="container py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
            <div>
              <div className="mb-4">
                <Link to="/"><img src={jobcamLogo} alt="JobCam Logo" className="h-10" width={191} height={40} /></Link>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Australia's leading provider of construction site cameras, solar security cameras, and remote monitoring solutions for the construction industry.
              </p>
              <a href="tel:1800562226" className="text-primary font-semibold hover:text-orange-glow transition-colors">1800 JOBCAM</a>
            </div>
            <div>
              <h3 className="font-display font-semibold text-foreground mb-4 text-base">Solutions</h3>
              <ul className="space-y-2">
                {footerSolutions.map((link) => (
                  <li key={link.to}><Link to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors">{link.label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display font-semibold text-foreground mb-4 text-base">Resources</h3>
              <ul className="space-y-2">
                {footerResources.map((link) => (
                  <li key={link.to}><Link to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors">{link.label}</Link></li>
                ))}
              </ul>
            </div>
            <div id="regional-cities">
              <h3 className="font-display font-semibold text-foreground mb-4 text-base">Regional Coverage</h3>
              <ul className="space-y-1">
                {locationGroups.map((group) => {
                  const cap = capitals.find(c => c.name === group.capital.label);
                  const stateCode = cap?.stateCode || group.capital.label;
                  const isExpanded = expandedFooterState === stateCode;
                  return (
                    <li key={group.capital.to}>
                      <button
                        onClick={() => setExpandedFooterState(isExpanded ? null : stateCode)}
                        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${stateCode} cities`}
                      >
                        <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        {stateCode}
                      </button>
                      {isExpanded && (
                        <ul className="pl-5 space-y-1 mt-1">
                          <li><Link to={group.capital.to} className="text-xs text-muted-foreground hover:text-primary transition-colors">{group.capital.label}</Link></li>
                          {group.cities.map((city) => (
                            <li key={city.to}><Link to={city.to} className="text-xs text-muted-foreground hover:text-primary transition-colors">{city.label}</Link></li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <h3 className="font-display font-semibold text-foreground mb-4 text-base">Company</h3>
              <ul className="space-y-2">
                {footerCompany.map((link) => (
                  <li key={link.to}><Link to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors">{link.label}</Link></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-8 border-t border-border">
            <h3 className="font-display font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Industry Resources</h3>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <li><a href="https://www.masterbuilders.com.au" target="_blank" rel="noopener" className="text-muted-foreground hover:text-primary transition-colors">Master Builders Australia</a></li>
              <li><a href="https://www.constructors.com.au" target="_blank" rel="noopener" className="text-muted-foreground hover:text-primary transition-colors">Australian Constructors Association</a></li>
              <li><a href="https://alga.com.au" target="_blank" rel="noopener" className="text-muted-foreground hover:text-primary transition-colors">Australian Local Government Association</a></li>
            </ul>
          </div>
          <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">© 2026 JobCam Pty Ltd. All rights reserved. ABN 98 661 836 264</p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="hover:text-primary transition-colors">Terms & Conditions of Hire</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
